import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { ToolSheet } from "./ToolSheet";
import type { ToolAdmin } from "@/types/tool";

type SortField = "name" | "created_at" | "updated_at" | "is_active" | "is_featured" | "is_coming_soon" | "category" | "type" | "order_index";

export const ToolsManagement = () => {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | "all">("all");
  const [typeFilter, setTypeFilter] = useState<string | "all">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [sortBy, setSortBy] = useState<SortField>("updated_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const [selected, setSelected] = useState<ToolAdmin | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-tools"],
    queryFn: async () => {
      const { data: tools, error } = await supabase
        .from("tools")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;

      const catIds = Array.from(new Set((tools || []).map((t: any) => t.category_id).filter(Boolean)));
      const typeIds = Array.from(new Set((tools || []).map((t: any) => t.tool_type_id).filter(Boolean)));

      const [{ data: categories }, { data: types }] = await Promise.all([
        catIds.length ? supabase.from("categories").select("id, name").in("id", catIds) : Promise.resolve({ data: [] as any[] }),
        typeIds.length ? supabase.from("tool_types").select("id, name").in("id", typeIds) : Promise.resolve({ data: [] as any[] }),
      ]);

      const catMap = new Map((categories || []).map((c: any) => [c.id, c]));
      const typeMap = new Map((types || []).map((t: any) => [t.id, t]));

      return (tools || []).map((t: any) => ({
        ...t,
        categories: t.category_id ? (catMap.get(t.category_id) || null) : null,
        tool_types: t.tool_type_id ? (typeMap.get(t.tool_type_id) || null) : null,
      })) as ToolAdmin[];
    },
  });

  const categories = useMemo(() => {
    const set = new Map<string, string>();
    (data || []).forEach((t) => {
      if (t.categories) set.set(t.categories.id, t.categories.name);
    });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const types = useMemo(() => {
    const set = new Map<string, string>();
    (data || []).forEach((t) => {
      if (t.tool_types) set.set(t.tool_types.id, t.tool_types.name);
    });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    let list = (data || []).filter((t) => {
      const matchesSearch = [t.name, t.description || "", t.categories?.name || "", t.tool_types?.name || ""].some((v) =>
        v.toLowerCase().includes(search.toLowerCase())
      );
      const matchesCat = categoryFilter === "all" || t.category_id === categoryFilter;
      const matchesType = typeFilter === "all" || t.tool_type_id === typeFilter;
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" ? t.is_active : !t.is_active);
      return matchesSearch && matchesCat && matchesType && matchesStatus;
    });

    list = list.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "created_at":
          return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir;
        case "updated_at":
          return (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * dir;
        case "is_active":
          return (Number(a.is_active) - Number(b.is_active)) * dir;
        case "is_featured":
          return (Number(a.is_featured) - Number(b.is_featured)) * dir;
        case "is_coming_soon":
          return (Number(a.is_coming_soon) - Number(b.is_coming_soon)) * dir;
        case "category":
          const catA = a.categories?.name || "Uncategorized";
          const catB = b.categories?.name || "Uncategorized";
          return catA.localeCompare(catB) * dir;
        case "type":
          const typeA = a.tool_types?.name || "—";
          const typeB = b.tool_types?.name || "—";
          return typeA.localeCompare(typeB) * dir;
        case "order_index":
          return (a.order_index - b.order_index) * dir;
        default:
          return (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * dir;
      }
    });

    return list;
  }, [data, search, categoryFilter, typeFilter, statusFilter, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const handleDelete = async (tool: ToolAdmin) => {
    if (!confirm(`Delete tool "${tool.name}"?`)) return;
    const { error } = await supabase.from("tools").delete().eq("id", tool.id);
    if (error) {
      console.error(error);
    } else {
      refetch();
      setSelected(null);
    }
  };

  const isRecent = (updated_at: string) => {
    const diffMin = (Date.now() - new Date(updated_at).getTime()) / 60000;
    return diffMin < 5; // highlight if updated within 5 minutes
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-xl">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search tools..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as any)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {types.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setSelected(null); setSheetOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add Tool
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tools ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("name")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Name {getSortIcon("name")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("category")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Category {getSortIcon("category")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("type")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Type {getSortIcon("type")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("order_index")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Order {getSortIcon("order_index")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("is_active")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Status {getSortIcon("is_active")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("is_featured")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Featured {getSortIcon("is_featured")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("created_at")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Created {getSortIcon("created_at")}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleSort("updated_at")}
                    className="h-auto p-0 font-medium hover:bg-transparent"
                  >
                    Updated {getSortIcon("updated_at")}
                  </Button>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paged as ToolAdmin[]).map((tool) => (
                <TableRow key={tool.id} className={isRecent(tool.updated_at) ? "bg-muted/30" : ""} onClick={() => { setSelected(tool); setSheetOpen(true); }}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {tool.icon && <span className="text-muted-foreground">{tool.icon}</span>}
                      {tool.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{tool.categories?.name || "Uncategorized"}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{tool.tool_types?.name || "—"}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-mono">{tool.order_index}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={tool.is_active ? "default" : "secondary"}>{tool.is_active ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  <TableCell>
                    {tool.is_featured ? (
                      <Badge variant="default">Featured</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>{new Date(tool.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(tool.updated_at).toLocaleDateString()}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setSelected(tool); setSheetOpen(true); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(tool)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">Page {page} of {totalPages} · {filtered.length} total</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ToolSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tool={selected}
        onSuccess={() => { setSheetOpen(false); refetch(); }}
      />
    </div>
  );
};
